import { useMemo } from "react";
import { IStatus } from "entities/RCA/StatusEntity/StatusEntityInterfaces";
import { IPriority } from "entities/RCA/PriorityEntity/PriorityEntityInterfaces";
import { useTranslation } from "react-i18next";
import { useRCAHistoryMainStyles } from "pages/Corporate/RCA/RCA.styles";
import {
    RCADueDateLog,
    RCAUserChangeLog,
    RCAStatusChangeLog,
    RCAPriorityChangeLog,
    RCATextChangeLog,
    RCAHistoryLog,
    ChangeTypeEnums,
    FieldNameEnumsIssue,
} from "pages/Corporate/RCA/components/RCAHistoryLogsComponents";
import { IHistoryLogIssue } from "entities/RCA/IssueEntity/IIssueEntityInterfaces";

export const RCAIssueHistoryLogItem = ({
    log,
    statuses,
    priorities,
}: {
    log: IHistoryLogIssue;
    statuses: IStatus[];
    priorities: IPriority[];
}) => {
    const classes = useRCAHistoryMainStyles();
    const { t } = useTranslation();

    const changeLog = useMemo(() => {
        if (log.field_name === FieldNameEnumsIssue.DUE_DATE || log.field_name === FieldNameEnumsIssue.INCIDENT_DATE) {
            return <RCADueDateLog newValue={log.new_value} oldValue={log.old_value} />;
        } else if (log.field_name === FieldNameEnumsIssue.OWNER_ID) {
            return <RCAUserChangeLog newValue={log.new_value} oldValue={log.old_value} />;
        } else if (log.field_name === FieldNameEnumsIssue.STATUS_ID) {
            const oldStatus = statuses.find((status) => status.status_id === Number(log.old_value));
            const newStatus = statuses.find((status) => status.status_id === Number(log.new_value));
            return <RCAStatusChangeLog oldStatus={oldStatus} newStatus={newStatus} />;
        } else if (log.field_name === FieldNameEnumsIssue.PRIORITY_ID) {
            const oldPriority = priorities.find((priority) => priority.priority_id === Number(log.old_value));
            const newPriority = priorities.find((priority) => priority.priority_id === Number(log.new_value));
            return <RCAPriorityChangeLog oldPriority={oldPriority} newPriority={newPriority} />;
        } else if (log.field_name === FieldNameEnumsIssue.TITLE || log.field_name === FieldNameEnumsIssue.DESCRIPTION) {
            return <RCATextChangeLog newValue={log.new_value} oldValue={log.old_value} />;
        } else if (log.field_name === FieldNameEnumsIssue.MILL_ID) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>{t("rca.history_audit_logs.mill_reassignment")}</span>
                </div>
            );
        } else if (
            log.field_name === FieldNameEnumsIssue.ACTION_PLAN_CREATED_AT ||
            log.field_name === FieldNameEnumsIssue.ACTION_PLAN_DELETED_AT
        ) {
            return (
                <div className={classes.historyChangeWrapper}>
                    <span className={classes.historyText}>
                        {t("rca.history_audit_logs.action_plan_id", { id: log.action_plan_id })}
                    </span>
                </div>
            );
        }
    }, [classes, log, statuses, priorities, t]);

    const changedField = useMemo(() => {
        if (log.field_name === FieldNameEnumsIssue.DUE_DATE) {
            return t("rca.history_audit_logs.due_date");
        } else if (log.field_name === FieldNameEnumsIssue.INCIDENT_DATE) {
            return t("rca.history_audit_logs.incident_date");
        } else if (log.field_name === FieldNameEnumsIssue.OWNER_ID) {
            return t("rca.history_audit_logs.owner");
        } else if (log.field_name === FieldNameEnumsIssue.STATUS_ID) {
            return t("rca.history_audit_logs.status");
        } else if (log.field_name === FieldNameEnumsIssue.PRIORITY_ID) {
            return t("rca.history_audit_logs.priority");
        } else if (log.field_name === FieldNameEnumsIssue.DESCRIPTION) {
            return t("rca.history_audit_logs.description");
        } else if (log.field_name === FieldNameEnumsIssue.TITLE) {
            return t("rca.history_audit_logs.short_description");
        } else if (log.field_name === FieldNameEnumsIssue.MILL_ID) {
            return t("rca.history_audit_logs.mill");
        } else if (
            log.field_name === FieldNameEnumsIssue.ACTION_PLAN_CREATED_AT ||
            log.field_name === FieldNameEnumsIssue.ACTION_PLAN_DELETED_AT
        ) {
            return t("rca.history_audit_logs.action_plan");
        } else if (
            log.field_name === FieldNameEnumsIssue.CREATED_AT ||
            log.field_name === FieldNameEnumsIssue.DELETED_AT
        ) {
            return t("rca.rca_dashboard.issue");
        } else if (
            log.field_name === FieldNameEnumsIssue.ISSUE_MEDIA_CREATED_AT ||
            log.field_name === FieldNameEnumsIssue.ISSUE_MEDIA_DELETED_AT
        ) {
            return t("rca.history_audit_logs.media");
        } else if (log.field_name === FieldNameEnumsIssue.MEETING_TYPE_ID) {
            return t("rca.history_audit_logs.meeting_type");
        }
        return "";
    }, [log, t]);

    const changeType = useMemo(() => {
        if (ChangeTypeEnums.INSERT === log.change_type) {
            return (
                <>
                    {t("rca.history_audit_logs.creation")} <strong>{changedField}</strong>
                </>
            );
        } else if (ChangeTypeEnums.UPDATE === log.change_type) {
            return (
                <>
                    {t("rca.history_audit_logs.updated_field")} <strong>{changedField}</strong>
                </>
            );
        } else if (ChangeTypeEnums.DELETE === log.change_type) {
            return (
                <>
                    {t("rca.history_audit_logs.deletion")} <strong>{changedField}</strong>
                </>
            );
        }
    }, [log, changedField, t]);

    return (
        <RCAHistoryLog
            changeType={changeType}
            changeLog={changeLog}
            changeDate={log.changed_at}
            changeUser={log.user}
        />
    );
};
